import { FEATURES } from "@/constants/features";
import store from "@/store";

/**
 * Two code paths by design — do NOT collapse them:
 *
 *   Normal path (hot path, runs on every render)
 *     Reads tenant_access.features, a flat feature_id list the backend
 *     precomputes at login (see authentication.py). No getters, no reactivity
 *     on featureConfig, no API call needed. This is the only path that runs
 *     for 99% of users 99% of the time.
 *
 *   Preview path (only when rolePreview.isPreviewActive)
 *     Simulates "what would this user see if they held roles X, Y" by
 *     intersecting those role_ids with featureConfig.roleFeatures from
 *     GET /api/features-config/. This requires featureConfig to be loaded
 *     — RolePreviewDropdown fetches it before enabling preview.
 *
 * Collapsing these would either force every canAccessFeature call to depend
 * on featureConfig being loaded (adds a fetch at login) or make the preview
 * wrong (tenant_access.features is the real user's grants, not the previewed
 * identity's grants).
 *
 * @param {string} feature - A FEATURES key to check access for.
 * @param {Object} [options]
 * @param {boolean} [options.superuserOverride=true] - Whether superusers
 *   automatically pass this check. Set false for checks that must reflect
 *   real grants even for superusers.
 * @returns {boolean}
 */
export function canAccessFeature(feature, options = {}) {
  const { superuserOverride = true } = options;
  const user = store.getters['auth/current_user'];

  if (!user) return false;

  // Superuser bypass is disabled during role preview so the preview actually
  // simulates a non-privileged identity. ACTION_ROLE_PREVIEW is the escape
  // hatch — the preview dropdown itself must stay visible to let the user
  // clear the preview.
  const isPreview = store.getters['rolePreview/isPreviewActive'];
  if (user.is_superuser && superuserOverride && (!isPreview || feature === FEATURES.ACTION_ROLE_PREVIEW)) return true;

  if (isPreview) {
    const roles = store.getters['tenant/current_tenant_roles'];
    if (!roles) return false;
    const roleFeatures = store.getters['featureConfig/roleFeatures'];
    const features = new Set();
    roles.forEach(role => {
      const rf = roleFeatures[role] || [];
      rf.forEach(f => features.add(f));
    });
    return features.has(feature);
  }

  const tenantSlug = store.getters['tenant/current_tenant_slug'];
  const tenantAccess = store.state.tenant.tenantAccess?.[tenantSlug];
  if (!tenantAccess) return false;
  return (tenantAccess.features || []).includes(feature);
}

export function canEdit() {
  return canAccessFeature(FEATURES.ACTION_EDIT);
}

export function canAccessEntity(entityId) {

  // If user can access all entities, allow access regardless of specific entity
  const canAccessAllEntities = canAccessFeature(FEATURES.DATA_ENTITIES_ALL);
  if (canAccessAllEntities) {
    return true;
  }

  // If user cannot access all entities, check specific entity access
  const availableEntityIds = store.getters['entity/availableEntityIds'];
  if (availableEntityIds.includes(entityId)) {
    return true;
  }

  return false;
}
